<script setup lang="ts">
import {computed} from "vue";
import useSwr from "@/core/composables/swr/useSwr";
import {loyaltyCardApi} from "@/container";
import {cacheKey} from "@/lib/cache";
import {currencyze} from "@/core/filters/Currencyze";
import {pluralize} from "@/core/filters/Pluralize";
import {numberFormat} from "@/core/filters/NumberFormat";
import {LoyaltyCardObjective} from "@/loyalty-card/interfaces/LoyaltyCard";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import Icon from "@/core/components/icon/Icon.vue";

const props = defineProps<{
    loyaltyCardId: string;
}>();

/** How many products each list previews: the eligible and the offered ones are read a page at a time. */
const PRODUCT_PREVIEW_SIZE = 5;

/** Above this threshold, one dot per product stops reading as a card and turns into noise. */
const MAX_DRAWN_STAMPS = 20;

const resourceId = Number(props.loyaltyCardId);

// The card itself, not its statistics: the overview computes those, the rules do not need them.
const {data: card, isLoading} = useSwr(`loyalty-cards/${resourceId}/rules`, () => loyaltyCardApi().read(resourceId));

const productParams = {size: PRODUCT_PREVIEW_SIZE, page: 1, type: 'all' as const};
const {data: products} = useSwr(
    cacheKey(`loyalty-cards/${resourceId}/products`, productParams),
    () => loyaltyCardApi().products(resourceId, productParams)
);

const excludedParams = {size: 1, page: 1, type: 'excluded' as const};
const {data: excludedProducts} = useSwr(
    cacheKey(`loyalty-cards/${resourceId}/products`, excludedParams),
    () => loyaltyCardApi().products(resourceId, excludedParams)
);

/** A card is always owned by a laboratory or a grouping; a pharmacy only applies its rules. */
const ownerName = computed(() => card.value?.organization?.morphable?.name ?? null);

/** Euros without decimals when there are none: "50 €" reads better than "50,00 €" in a rule. */
const formatEuro = (value: number) => currencyze(value, Number.isInteger(value) ? 0 : 2);

const thresholdLabel = (objective: LoyaltyCardObjective) => {
    if (objective.type?.slug === "amount") {
        return `${formatEuro(objective.value)} d'achats éligibles`;
    }

    return `${objective.value} ${pluralize("produit éligible", objective.value)}`;
};

const rewardLabel = (objective: LoyaltyCardObjective) => {
    const {reward} = objective;

    switch (reward?.type?.slug) {
        case "product":
            return `${reward.value} ${pluralize("produit offert", reward.value)}`;
        case "point":
            return `${reward.value} ${pluralize("point", reward.value)}`;
        case "discount":
            return `${formatEuro(reward.value)} de remise`;
        default:
            return reward?.teaser ?? "";
    }
};

/** One dot per product on a stamp card, the way the patient sees it in the app. */
const stamps = (objective: LoyaltyCardObjective) => (
    objective.type?.slug !== "amount"
    && objective.reward?.type?.slug === "product"
    && objective.value <= MAX_DRAWN_STAMPS
        ? objective.value
        : 0
);

/**
 * The card's points target and what it converts into. Its objectives then
 * only say how points are earned, so they read inside it rather than as
 * rewards of their own.
 */
const point = computed(() => {
    const scale = card.value?.point;

    if (!scale?.value) {
        return null;
    }

    const reward = scale.reward;

    if (!reward) {
        return {target: scale.value, reward: scale.teaser, rate: null};
    }

    // A scale may convert points into products rather than euros.
    if (reward.type?.slug === "product") {
        return {
            target: scale.value,
            reward: `${reward.value} ${pluralize("produit offert", reward.value)}`,
            rate: null
        };
    }

    return {
        target: scale.value,
        reward: `${formatEuro(reward.value)} de remise`,
        // A point is often worth a fraction of a cent: three decimals keep 0,025 € from rounding up.
        rate: currencyze(reward.value / scale.value, 3)
    };
});

/** Smallest threshold first, so a card with several tiers reads in the order a patient reaches them. */
const objectives = computed(() => [...(card.value?.objectives ?? [])].sort((a, b) => a.value - b.value));

const earningObjectives = computed(() => (
    point.value ? objectives.value.filter(objective => objective.reward?.type?.slug === "point") : []
));

const rewardObjectives = computed(() => objectives.value.filter(objective => !earningObjectives.value.includes(objective)));

/** Whether a product reward can be earned again. Points are earned at every purchase, the question does not arise. */
const productRewardRepeats = computed(() => {
    const reward = rewardObjectives.value.find(objective => objective.reward?.type?.slug === "product")?.reward;

    return reward?.is_repeatable ?? null;
});

/** Only a card that offers products has any to list. */
const offersProducts = computed(() => objectives.value.some(objective => objective.reward?.type?.slug === "product"));

const rewardProductParams = {size: PRODUCT_PREVIEW_SIZE, page: 1};
const {data: rewardProducts} = useSwr(
    computed(() => (offersProducts.value ? cacheKey(`loyalty-cards/${resourceId}/reward-products`, rewardProductParams) : null)),
    () => loyaltyCardApi().rewardProducts(resourceId, rewardProductParams)
);

const rewardProductCount = computed(() => rewardProducts.value?.meta?.total ?? 0);
const rewardProductPreview = computed(() => rewardProducts.value?.data ?? []);
/** The offered products the preview leaves out. */
const hiddenRewardProductCount = computed(() => Math.max(0, rewardProductCount.value - rewardProductPreview.value.length));

const productCount = computed(() => products.value?.meta?.total ?? null);
/** A card range often runs into the thousands: "183 716", not "183716". */
const formattedProductCount = computed(() => (productCount.value !== null ? numberFormat(productCount.value, 0) : null));
const excludedCount = computed(() => excludedProducts.value?.meta?.total ?? 0);
const productPreview = computed(() => products.value?.data ?? []);
</script>

<template>
  <BaseContainer>
    <BaseFlexSpinner v-if="isLoading" />
    <div v-else-if="card" class="m-6 flex flex-col gap-5">
      <div>
        <h2 class="text-xl font-semibold text-primary">
          Règles de fonctionnement
        </h2>
        <p class="text-sm text-slate-500 mt-0.5">
          Ce qui fait avancer une carte client et ce qu'elle rapporte.
        </p>
      </div>

      <div
        v-if="ownerName"
        class="flex items-center gap-3 rounded-xl bg-lightPurple-lightest px-4 py-3.5 text-primary-dark"
      >
        <div class="w-9 h-9 rounded-full bg-white flex items-center justify-center shrink-0">
          <Icon name="mdi-lock-outline" :size="18" color="#3c008a" />
        </div>
        <p class="text-sm">
          <span class="font-semibold">Ces règles sont fixées par {{ ownerName }}.</span>
          Elles s'appliquent de la même façon dans toutes les pharmacies participantes et ne se modifient pas depuis votre pharmacie.
        </p>
      </div>

      <section class="bg-white rounded-lg shadow-custom p-6 flex flex-col gap-5">
        <h3 class="text-lg font-semibold text-primary">
          Règle du programme
        </h3>

        <div
          v-for="objective in rewardObjectives"
          :key="objective.id"
          class="flex items-stretch gap-4"
        >
          <div class="flex-1 rounded-xl bg-primary-lightest p-5 flex flex-col gap-1.5">
            <span class="text-sm text-slate-500">Le patient cumule</span>
            <span class="text-2xl font-semibold leading-tight text-primary">{{ thresholdLabel(objective) }}</span>
            <div v-if="stamps(objective)" class="flex flex-wrap gap-1.5 mt-1.5">
              <span
                v-for="stamp in stamps(objective)"
                :key="stamp"
                class="w-4 h-4 rounded-full border-2 border-solid border-primary"
              />
            </div>
          </div>
          <div class="flex items-center">
            <Icon name="mdi-arrow-right" :size="28" color="#3513e2" />
          </div>
          <!-- Not bg-secondary: Vuetify has a class of that name forcing its own text colour. -->
          <div class="flex-1 rounded-xl bg-green-light p-5 flex flex-col gap-1.5 text-primary-dark">
            <span class="text-sm">Il reçoit</span>
            <span class="text-2xl font-semibold leading-tight">{{ rewardLabel(objective) }}</span>
            <span v-if="objective.reward?.products_count" class="text-base">
              au choix parmi {{ objective.reward.products_count }} {{ pluralize('référence', objective.reward.products_count) }}
            </span>
          </div>
        </div>

        <div v-if="point" class="flex items-stretch gap-4">
          <div class="flex-1 rounded-xl bg-primary-lightest p-5 flex flex-col gap-1.5">
            <span class="text-sm text-slate-500">Le patient cumule</span>
            <span class="text-2xl font-semibold leading-tight text-primary">{{ point.target }} points</span>
            <span
              v-for="objective in earningObjectives"
              :key="objective.id"
              class="text-base"
            >
              {{ thresholdLabel(objective) }} = {{ rewardLabel(objective) }}
            </span>
          </div>
          <div class="flex items-center">
            <Icon name="mdi-arrow-right" :size="28" color="#3513e2" />
          </div>
          <div class="flex-1 rounded-xl bg-green-light p-5 flex flex-col gap-1.5 text-primary-dark">
            <span class="text-sm">Il reçoit</span>
            <span class="text-2xl font-semibold leading-tight">{{ point.reward }}</span>
            <span v-if="point.rate" class="text-base">1 point = {{ point.rate }}</span>
          </div>
        </div>

        <dl v-if="productRewardRepeats !== null || point?.rate" class="flex flex-col">
          <div v-if="productRewardRepeats !== null" class="grid grid-cols-[260px_minmax(0,1fr)] gap-4 py-3 border-t border-solid border-slate-200">
            <dt class="text-sm text-slate-500">
              Récompense
            </dt>
            <dd>{{ productRewardRepeats ? 'Renouvelable à chaque palier atteint' : 'Une seule fois par carte' }}</dd>
          </div>
          <div v-if="point?.rate" class="grid grid-cols-[260px_minmax(0,1fr)] gap-4 py-3 border-t border-solid border-slate-200">
            <dt class="text-sm text-slate-500">
              Valeur d'un point
            </dt>
            <dd>{{ point.rate }}</dd>
          </div>
        </dl>
      </section>

      <div class="flex items-stretch gap-5">
        <section class="flex-1 bg-white rounded-lg shadow-custom p-6 flex flex-col gap-4">
          <div class="flex items-center gap-2.5">
            <Icon name="mdi-package-variant-closed" :size="20" color="#3513e2" />
            <h3 class="text-lg font-semibold text-primary">
              Produits éligibles
            </h3>
            <span
              v-if="productCount !== null"
              class="rounded-full bg-lightPurple-lightest px-2.5 text-sm font-semibold text-primary-dark"
            >
              {{ formattedProductCount }}
            </span>
          </div>
          <p class="text-sm text-slate-500">
            Seuls les achats de ces produits font avancer la carte.
            <template v-if="excludedCount">
              {{ excludedCount }} {{ pluralize('produit', excludedCount) }} {{ excludedCount > 1 ? 'sont exclus' : 'est exclu' }} par votre pharmacie.
            </template>
          </p>
          <ul v-if="productPreview.length" class="flex flex-col">
            <li
              v-for="product in productPreview"
              :key="product.id"
              class="flex items-center justify-between gap-3 py-2.5 border-t border-solid border-slate-200 last:border-b"
            >
              <span>{{ product.name }}</span>
              <span
                v-if="product.healthcenter_product?.is_included === false"
                class="shrink-0 rounded-full bg-gray-light px-2.5 text-sm text-slate-500"
              >
                Exclu par votre pharmacie
              </span>
            </li>
          </ul>
          <router-link
            :to="{name: 'program.loyalty-card.products', params: {loyaltyCardId}}"
            class="self-start inline-flex items-center gap-1.5 min-h-[44px] text-sm font-semibold text-primary hover:opacity-75"
          >
            {{ productCount ? `Voir les ${formattedProductCount} produits éligibles` : 'Voir les produits éligibles' }}
            <Icon name="mdi-arrow-right" :size="16" color="#3513e2" />
          </router-link>
        </section>

        <section
          v-if="rewardProductCount"
          class="flex-1 bg-white rounded-lg shadow-custom p-6 flex flex-col gap-4"
        >
          <div class="flex items-center gap-2.5">
            <Icon name="mdi-gift-outline" :size="20" color="#3513e2" />
            <h3 class="text-lg font-semibold text-primary">
              Produits offerts en récompense
            </h3>
            <span class="rounded-full bg-secondary-lightest px-2.5 text-sm font-semibold text-primary-dark">
              {{ numberFormat(rewardProductCount, 0) }}
            </span>
          </div>
          <p class="text-sm text-slate-500">
            Une fois le seuil atteint, le patient choisit un de ces produits.
          </p>
          <ul class="flex flex-col">
            <li
              v-for="product in rewardProductPreview"
              :key="product.id"
              class="py-2.5 border-t border-solid border-slate-200 last:border-b"
            >
              {{ product.name ?? 'Produit sans nom' }}
            </li>
          </ul>
          <p v-if="hiddenRewardProductCount" class="text-sm text-slate-500">
            et {{ hiddenRewardProductCount }} {{ pluralize('autre produit', hiddenRewardProductCount) }}
          </p>
        </section>
      </div>
    </div>
    <p v-else class="m-6">
      Les règles de cette carte n'ont pas pu être chargées.
    </p>
  </BaseContainer>
</template>
